import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  Tooltip,
  Grid,
  ListSubheader,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import BillSummaryReportService from 'services/BillSummaryReportService';
import SalespersonsService from 'services/SalesPersonService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import BillDetailsDialog from 'components/popups/BillDetailsDialog';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import CustomerService from 'services/CustomerService';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { STORAGE_KEYS, SelectFilterStyle, SALE_SOURCES, SALE_STATUS, PRINT_TYPES } from 'const';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import { TerminalContext } from 'Context/TerminalContext';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import { filterEndDate } from 'components/utils/FilterEndDate';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import { getFilteredCSVColumns } from 'components/utils/FilteredColumns';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';
import { useReportPrint } from 'hooks/useReportPrint';

const getSaleEditHistory = (sale = {}) => {
  let config = sale?.config;
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config);
    } catch (e) {
      config = {};
    }
  }
  return config?.history || [];
};

const getEditAuthorInfo = (entry = {}) => {
  const explicitType = entry.editor_type ? String(entry.editor_type).trim().toLowerCase() : null;
  const userName = entry.user_name ? String(entry.user_name).trim() : null;
  const salespersonName = entry.salesperson_name ? String(entry.salesperson_name).trim() : null;

  if (explicitType === 'user') {
    return { editorType: 'user', editorName: userName || salespersonName || null };
  }

  if (explicitType === 'cashier') {
    return { editorType: 'cashier', editorName: salespersonName || userName || null };
  }

  // Infer strictly from presence of explicit user/salesperson attributes on the entry
  if (entry.user_id || userName) {
    return { editorType: 'user', editorName: userName || null };
  }

  if (entry.salesperson_id || salespersonName) {
    return { editorType: 'cashier', editorName: salespersonName || null };
  }

  // No author recorded on this entry (legacy edit) — do not guess
  return { editorType: null, editorName: null };
};

const enrichSalesEditItems = (reportItems = []) => {
  if (!reportItems?.length) return [];

  const expandedRows = [];

  reportItems.forEach((item) => {
    const billNo = String(item.bill_no || '').trim();
    const historyList = getSaleEditHistory(item);

    if (historyList.length > 0) {
      historyList.forEach((entry, idx) => {
        const { editorType, editorName } = getEditAuthorInfo(entry);
        const byText = editorName
          ? editorType
            ? ` by ${editorType} ${editorName}`
            : ` by ${editorName}`
          : '';
        const editSuffix = historyList.length > 1 ? ` (Edit ${idx + 1})` : '';

        expandedRows.push({
          ...item,
          id: item.id,
          sale_id: item.id,
          row_id: `${item.id}-edit-${idx}`,
          date: entry.timestamp ? DateTimeHelpers.getDate(entry.timestamp) : item.date,
          time: entry.timestamp || item.time,
          description: `Edited Sale '#${billNo}'${editSuffix}${byText}`,
          salesperson_name: editorName,
          from_payable: Number(entry.from_payable ?? item.from_payable ?? item.total ?? 0),
          to_payable: Number(entry.to_payable ?? item.to_payable ?? item.total ?? 0),
        });
      });
    }
  });

  return expandedRows;
};

function SalesEditReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');

  const { shop } = useContext(ShopContext);
  const { terminals: terminalsFromContext } = useContext(TerminalContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(getDefaultStartTime());
  const [filterToDate, setFilterToDate] = useState(filterEndDate());
  const [sorting, setSorting] = useState('default');
  const [filterStatus, setFilterStatus] = useState(SALE_STATUS.active);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    sorting,
    filterStatus,
  });
  const [filterTerminal, setFilterTerminal] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState([]);
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchorEl(null);
  };

  const [filterCustomer, setFilterCustomer] = useState([]);
  const [customerSearchText, setCustomerSearchText] = useState('');
  const [filterCustomerGroup, setFilterCustomerGroup] = useState([]);
  const [filterSource, setFilterSource] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const terminalMap = useMemo(
    () => Object.fromEntries(terminalsFromContext.map((terminal) => [terminal.id, terminal.name])),
    [terminalsFromContext]
  );
  const customerMap = useMemo(
    () => Object.fromEntries((customers || []).map((customer) => [customer.id, customer.name])),
    [customers]
  );
  const salesPersonMap = useMemo(
    () => Object.fromEntries(salesPersons.map((person) => [person.id, person.name])),
    [salesPersons]
  );
  const customerGroupMap = useMemo(
    () => Object.fromEntries(customerGroups.map((group) => [group.id, group.name])),
    [customerGroups]
  );
  const saleSourceMap = useMemo(() => Object.fromEntries(SALE_SOURCES.map((source) => [source.id, source.label])), []);

  const getOldBillAmount = (datum) => {
    if (!datum) return 0;
    return Number(datum.from_payable ?? datum.total ?? 0);
  };

  const getNewBillAmount = (datum) => {
    if (!datum) return 0;
    return Number(datum.to_payable ?? datum.total ?? 0);
  };

  const formatEditTime = (timeVal) => {
    if (!timeVal) return '-';

    if (typeof timeVal === 'string' && timeVal.includes(':') && !timeVal.includes('T') && !timeVal.includes('-')) {
      return DateTimeHelpers.formatServerTimeSegment(timeVal);
    }

    const d = new Date(timeVal);
    if (!isNaN(d.getTime())) {
      return d
        .toLocaleTimeString([], {
          hour12: true,
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
        })
        .toUpperCase();
    }

    return '-';
  };

  const CSV_COLUMNS = {
    bill_no: {
      title: 'Bill No',
      getValue: (datum) => datum.bill_no,
    },
    date: 'Date',
    time: {
      title: 'Time',
      getValue: (datum) => formatEditTime(datum.time || datum.edited_at),
    },
    description: {
      title: 'Description',
      getValue: (datum) => datum.description || datum.remark || `Edited Sale '#${datum.bill_no || '-'}'`,
    },
    from_payable: {
      title: 'From Payable',
      getValue: (datum) => ShopsHelper.getAmountFormatted(shop, getOldBillAmount(datum)),
    },
    to_payable: {
      title: 'To Payable',
      getValue: (datum) => ShopsHelper.getAmountFormatted(shop, getNewBillAmount(datum)),
    },
    customer_name: 'Customer',
  };

  function getDefaultStartTime() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      sorting !== prevFilterData.sorting ||
      filterTerminal !== prevFilterData.filterTerminal ||
      filterSalesperson !== prevFilterData.filterSalesperson ||
      filterCustomerGroup !== prevFilterData.filterCustomerGroup ||
      filterSource !== prevFilterData.filterSource ||
      filterStatus !== prevFilterData.filterStatus ||
      filterCustomer !== prevFilterData.filterCustomer
    );
  }, [
    filterFromDate,
    filterToDate,
    sorting,
    filterTerminal,
    filterSalesperson,
    filterCustomerGroup,
    filterSource,
    filterStatus,
    filterCustomer,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    prevFilterData.sorting,
    prevFilterData.filterTerminal,
    prevFilterData.filterSalesperson,
    prevFilterData.filterCustomerGroup,
    prevFilterData.filterSource,
    prevFilterData.filterStatus,
    prevFilterData.filterCustomer,
  ]);

  useEffect(() => {
    getCustomers();
    getSalespersons();
    getCustomerGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    generateSummeryReport();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Sales Edit Report'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredItems,
      {
        [tCommon('Total From Payable')]: `${shop?.currency || ''} ${summary.totalFromPayable}`,
        [tCommon('Total To Payable')]: `${shop?.currency || ''} ${summary.totalToPayable}`,
      }
    );
  };

  const getCustomerGroups = async () => {
    try {
      const res = await CustomerService.getCustomerGroups();
      setCustomerGroups(res || []);
    } catch (err) {
      console.error('Failed to fetch customer groups', err);
    }
  };

  const generateSummeryReport = () => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('SALES_EDIT_SUMMARY');
    setFilterFromDate(dynamicStartDate);
    setFilterToDate(dynamicEndDate);
    loadBillSummaryReports({
      startTime: dynamicStartDate,
      endTime: dynamicEndDate,
      sort: sorting,
      cashier_ids: filterSalesperson,
      terminal_ids: filterTerminal,
      customer_group_ids: filterCustomerGroup,
      sources: filterSource,
      status: filterStatus,
      customer_ids: filterCustomer,
    });
    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: dynamicStartDate,
      filterToDate: dynamicEndDate,
      sorting,
      filterSalesperson,
      filterTerminal,
      filterCustomerGroup,
      filterSource,
      filterStatus,
      filterCustomer,
    });
  };



  const loadBillSummaryReports = async ({
    startTime,
    endTime,
    sort = sorting,
    cashier_ids = filterSalesperson,
    terminal_ids = filterTerminal,
    customer_group_ids = filterCustomerGroup,
    sources = filterSource,
    status = filterStatus,
    customer_ids = filterCustomer,
  } = {}) => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('SALES_EDIT_SUMMARY');

    const start = startTime || dynamicStartDate;
    const end = endTime || dynamicEndDate;

    if (DateTimeHelpers.reportDateLimit(start, end)) {
      setDateLimitPopup(true);
      return;
    }

    setLoadingIndicator(true);
    try {
      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(start),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(end),
        terminal_ids: terminal_ids.length ? terminal_ids : null,
        cashier_ids: cashier_ids.length ? cashier_ids : null,
        sort_bill_num: sort === 'default' ? undefined : sort,
        customer_group_ids: customer_group_ids.length ? customer_group_ids : null,
        sources: sources.length ? sources : null,
        status,
        customer_ids: customer_ids.length ? customer_ids : null,
      };

      const resp = await BillSummaryReportService.getSalesEditReport(data);
      const reportItems = enrichSalesEditItems(resp);
      setItems(reportItems);
      setFilteredItems(reportItems);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }

    setLoadingIndicator(false);
  };

  const getCustomers = async () => {
    try {
      const res = await CustomerService.getCustomers();
      setCustomers(res || []);
    } catch (err) {
      console.error('Failed to fetch customers', err);
    }
  };

  const getSalespersons = async () => {
    try {
      const res = await SalespersonsService.getSalesPersons();
      setSalesPersons(res || []);
    } catch (err) {
      console.error('Failed to fetch salespersons', err);
    }
  };

  const toCsvBtnPressed = async () => {
    const filteredCsvColumns = getFilteredCSVColumns(
      CSV_COLUMNS,
      optionalColumns,
      STORAGE_KEYS.SALES_REPORT_COLUMN_FILTER
    );
    const csv = CsvHelper.getString(filteredItems, filteredCsvColumns);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `SALES-EDIT-REPORT-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const handleCloseSaleDetailsDialog = () => {
    setSelectedBill(null);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const searchVal = value.trim().toLowerCase();
    if (!searchVal) {
      setFilteredItems(items);
      return;
    }

    setFilteredItems(
      items.filter((item) => {
        const searchFields = [
          item.bill_no,
          item.date,
          item.description,
          item.remark,
          item.customer_name,
          item.salesperson_name,
        ];
        return searchFields.some((field) => field && String(field).toLowerCase().includes(searchVal));
      })
    );
  };

  const _getBillReportSummary = (items) => {
    let totalFromPayable = 0;
    let totalToPayable = 0;
    (items || []).forEach((item) => {
      totalFromPayable += Number(getOldBillAmount(item));
      totalToPayable += Number(getNewBillAmount(item));
    });

    return {
      totalFromPayable: ShopsHelper.getAmountFormatted(shop, totalFromPayable),
      totalToPayable: ShopsHelper.getAmountFormatted(shop, totalToPayable),
    };
  };

  const headerData = [
    {
      label: tCommon('Bill No'),
      id: 'bill_no',
      type: 'callback',
      viewRender: (obj) => obj.bill_no,
    },
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'text',
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => formatEditTime(item.time || item.edited_at),
    },
    {
      label: tCommon('Description'),
      id: 'description',
      type: 'callback',
      viewRender: (item) => item.description || item.remark || `Edited Sale '#${item.bill_no || '-'}'`,
    },
    {
      label: tCommon('From Payable'),
      id: 'from_payable',
      type: 'callback',
      viewRender: (item) =>
        `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, getOldBillAmount(item))}`,
    },
    {
      label: tCommon('To Payable'),
      id: 'to_payable',
      type: 'callback',
      viewRender: (item) =>
        `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, getNewBillAmount(item))}`,
    },
    {
      label: tCommon('Actions'),
      id: 'download',
      cssClass: 'no-print',
      cssClassHeader: 'no-print',
      type: 'callback',
      viewRender: (bill) => (
        <Grid className={styles.btnGroup}>
          <Tooltip title={tCommon('View Items')} onClick={() => setSelectedBill(bill)}>
            <RemoveRedEyeIcon className={styles.iconBtn} />
          </Tooltip>
        </Grid>
      ),
    },
  ];

  const optionalColumns = {
    bill_no: true,
    date: true,
    time: true,
    description: true,
    from_payable: true,
    to_payable: true,
    download: true,
  };

  const summary = _getBillReportSummary(filteredItems);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a' }}
              onClick={toCsvBtnPressed}
            >
              <ImportExportIcon className={styles.actionBtnIcon} />
              {tCommon('CSV')}
            </Button>
            <IconButton
              onClick={handleExportMenuOpen}
              style={{
                backgroundColor: '#0284c7',
                color: '#fff',
                borderRadius: '4px',
                width: '40px',
                height: '40px',
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={exportMenuAnchorEl}
              open={Boolean(exportMenuAnchorEl)}
              onClose={handleExportMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleExportMenuClose();
                  handlePrintReport();
                }}
              >
                <PrintIcon style={{ marginRight: '8px', fontSize: '20px' }} />
                {tCommon('Print')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleExportMenuClose();
                  toCsvBtnPressed();
                }}
              >
                <ImportExportIcon style={{ marginRight: '8px', fontSize: '20px' }} />
                {tCommon('CSV')}
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tReport('Sales Edit Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
                className={styles.searchInput}
                placeholder={tReport('Search by bill no, date')}
              />
              <Tooltip title={tCommon('Search')} placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.filterDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="SALES_EDIT_SUMMARY"
            />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Terminal')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterTerminal}
                  onChange={(e) => setFilterTerminal(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterTerminal) => {
                    if (filterTerminal.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterTerminal.map((id) => terminalMap[id]).filter(Boolean);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {terminalsFromContext.map((terminal) => (
                    <MenuItem key={terminal.id} value={terminal.id}>
                      <Checkbox checked={filterTerminal.includes(terminal.id)} color="primary" />
                      <ListItemText primary={terminal.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          {ShopsHelper.hasPremiumAccess(shop) && (
            <>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Customer')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      displayEmpty
                      value={filterCustomer}
                      onChange={(e) => setFilterCustomer(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(filterCustomer) => {
                        if (filterCustomer.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = filterCustomer.map((id) => customerMap[id]).filter(Boolean);
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      <ListSubheader>
                        <TextField
                          size="small"
                          autoFocus
                          placeholder={tCommon('Search')}
                          fullWidth
                          InputProps={{
                            startAdornment: <SearchIcon style={{ color: 'grey' }} />,
                          }}
                          onChange={(e) => setCustomerSearchText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key !== 'Escape') {
                              e.stopPropagation();
                            }
                          }}
                        />
                      </ListSubheader>
                      {customers &&
                        customers.length > 0 &&
                        customers
                          .filter((customer) => customer.name.toLowerCase().includes(customerSearchText.toLowerCase()))
                          .map((customer) => (
                            <MenuItem key={customer.id} value={customer.id}>
                              <Checkbox checked={filterCustomer.includes(customer.id)} color="primary" />
                              <ListItemText primary={customer?.name} />
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Salesperson')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      displayEmpty
                      value={filterSalesperson}
                      onChange={(e) => setFilterSalesperson(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(filterSalesperson) => {
                        if (filterSalesperson.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = filterSalesperson.map((id) => salesPersonMap[id]).filter(Boolean);
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      {salesPersons.map((salesperson) => (
                        <MenuItem key={salesperson.id} value={salesperson.id}>
                          <Checkbox checked={filterSalesperson.includes(salesperson.id)} color="primary" />
                          <ListItemText primary={salesperson?.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
            </>
          )}
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Bill no order')}</label>
            </div>
            <div>
              <TextField
                select
                name="billNo"
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 135 }}
                value={sorting}
                onChange={(event) => {
                  setSorting(event.target.value);
                }}
                displayEmpty
              >
                <MenuItem value={'default'}>
                  <em>{tCommon('Default')}</em>
                </MenuItem>
                <MenuItem value={'DESC'}>{tCommon('Descending')}</MenuItem>
                <MenuItem value={'ASC'}>{tCommon('Ascending')}</MenuItem>
              </TextField>
            </div>
          </div>
          {ShopsHelper.hasPremiumAccess(shop) && (
            <div className={styles.filterDiv}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Customer Group')}</label>
              </div>
              <div>
                <FormControl>
                  <Select
                    multiple
                    displayEmpty
                    value={filterCustomerGroup}
                    onChange={(e) => setFilterCustomerGroup(e.target.value)}
                    input={<OutlinedInput />}
                    style={{ width: 135, height: 40 }}
                    renderValue={(selected) => {
                      if (selected.length === 0) return <span>{tCommon('All')}</span>;
                      const selectedNames = selected.map((id) => customerGroupMap[id]).filter(Boolean);
                      return selectedNames.join(', ');
                    }}
                    MenuProps={SelectFilterStyle}
                  >
                    {customerGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        <Checkbox checked={filterCustomerGroup.includes(group.id)} color="primary" />
                        <ListItemText primary={group.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          )}
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Sale source')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  multiple
                  displayEmpty
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(selected) => {
                    if (selected.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = selected.map((id) => saleSourceMap[id]).filter(Boolean);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {SALE_SOURCES.map((source) => (
                    <MenuItem key={source.id} value={source.id}>
                      <Checkbox checked={filterSource.includes(source.id)} color="primary" />
                      <ListItemText primary={source.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Sale Status')}</label>
            </div>
            <div>
              <TextField
                select
                name="status"
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 135 }}
                value={filterStatus}
                onChange={(event) => {
                  setFilterStatus(event.target.value);
                }}
              >
                {Object.values(SALE_STATUS).map((s) => {
                  return <MenuItem key={s} value={s}>{tCommon(s)}</MenuItem>;
                })}
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Generate Report')}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generateSummeryReport}
                disabled={isShowGenerateButton === false || loadingIndicator}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon('Generate')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : (
        items &&
        (filteredItems && filteredItems.length ? (
          <>
            <div className={styles.summaryContainer}>
              <div className={`${styles.summaryCard} ${styles.cardBlue}`}>
                <span className={styles.cardTitle}>{tCommon('Total From Payable')}</span>
                <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalFromPayable}`}</div>
                <div className={styles.cardDetails}>
                  <span>
                    {tCommon('Total Edits')}: <strong className={styles.detailsValue}>{filteredItems.length}</strong>
                  </span>
                </div>
              </div>

              <div className={`${styles.summaryCard} ${styles.cardGreen}`}>
                <span className={styles.cardTitle}>{tCommon('Total To Payable')}</span>
                <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalToPayable}`}</div>
              </div>
            </div>

            <div className={styles.tableContainer}>
              <Grayout open={isShowGenerateButton} />
              <DataTable
                columns={headerData}
                rows={filteredItems ? filteredItems : items}
                rowKey="row_id"
                summary={{
                  date: tCommon('Total'),
                  from_payable: `${shop?.currency || ''} ${summary.totalFromPayable}`,
                  to_payable: `${shop?.currency || ''} ${summary.totalToPayable}`,
                }}
                columnFilterId={STORAGE_KEYS.SALES_REPORT_COLUMN_FILTER}
                optionalColumns={optionalColumns}
                pagination={true}
              />
            </div>
          </>
        ) : (
          !loadingIndicator && (
            <Info
              severity="warning"
              title={tReport('Oh no, there are no sales matching to your filter')}
              content={tReport(
                'There are no sales matching to your filter. Please verify your inputs to generate report.'
              )}
            />
          )
        ))
      )}

      {selectedBill && (
        <BillDetailsDialog
          handleClose={handleCloseSaleDetailsDialog}
          selectedSale={selectedBill}
          customers={customers}
        />
      )}

      {dateLimitPopup && (
        <DateLimitDialog open={dateLimitPopup} handleClose={() => setDateLimitPopup(false)} />
      )}

      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
      {loadingIndicator && <Grayout />}
    </div>
  );
}

export default withConsoleBase(SalesEditReport);
